import { useQuery } from "@tanstack/react-query";
import { listAllSearchProducts, type SearchProductRow } from "@/lib/products.functions";

export type { SearchProductRow };

/**
 * Loads ALL active, in-stock products once and keeps them in memory
 * (staleTime: Infinity) so search/filter happens client-side without
 * hitting the database on every keystroke.
 *
 * Pass `enabled: false` to defer the (heavy) catalog download until the user
 * actually opens/uses search — avoids a large serverFn request on every page
 * load.
 */
export function useSearchProducts(enabled: boolean = true) {
  const query = useQuery({
    queryKey: ["products", "all-search"],
    queryFn: () => listAllSearchProducts(),
    enabled,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
  });

  return {
    products: (query.data?.products ?? []) as SearchProductRow[],
    isLoading: query.isLoading,
    isReady: !!query.data,
    error: query.data?.error ?? (query.error ? String(query.error) : null),
  };
}
